import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { OrderConfirmationScreen } from '@/components/customer/OrderConfirmationScreen';
import { OrderSummary } from '@/components/customer/OrderSummary';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrder, useSubmitOrder } from '@/queries/useCart';
import { useCartStore } from '@/store/cartStore';
import { useSessionStore } from '@/store/sessionStore';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type OrderReviewState = 'loading' | 'summary' | 'confirming' | 'confirmed';

export default function OrderReview() {
  const navigate = useNavigate();
  const [state, setState] = useState<OrderReviewState>('loading');
  const [submittedOrderId, setSubmittedOrderId] = useState<string | null>(null);
  const [eta, setEta] = useState<number>(20);

  const { orderId, isSessionValid } = useSessionStore();
  const { isCartEmpty, clearCart, setOrder } = useCartStore();
  const submitOrderMutation = useSubmitOrder();
  const { data: orderData, isLoading: isLoadingOrder } = useOrder(submittedOrderId || null);
  
  // Fetch current order to verify it has items before submission
  const { data: currentOrderData, refetch: refetchCurrentOrder } = useOrder(orderId || null);
  
  // Sync cart with backend order on page load
  useEffect(() => {
    if (currentOrderData && orderId) {
      // Backend is source of truth - sync cart store with backend order
      setOrder({
        orderId: currentOrderData.orderId,
        items: currentOrderData.items,
        total: currentOrderData.total,
      });
    }
  }, [currentOrderData, orderId, setOrder]);

  // Route guards
  useEffect(() => {
    // Check session validity
    if (!isSessionValid()) {
      navigate('/voice', { replace: true });
      return;
    }

    // Check cart has items
    if (isCartEmpty()) {
      navigate('/menu', { replace: true });
      return;
    }

    // Check orderId exists
    if (!orderId) {
      navigate('/menu', { replace: true });
      return;
    }

    // All checks passed, show summary
    setState('summary');
  }, [isSessionValid, isCartEmpty, orderId, navigate]);

  const handleEdit = () => {
    // Navigate back to menu with cart drawer open
    navigate('/menu');
  };

  const handleConfirm = async () => {
    if (!orderId) return;

    setState('confirming');

    try {
      // Refetch order from backend to ensure we have latest data before submission
      const refetchResult = await refetchCurrentOrder();
      const latestOrder = refetchResult.data;
      
      // Verify order has items before submission
      if (!latestOrder || !latestOrder.items || latestOrder.items.length === 0) {
        setState('summary');
        throw new Error('Order has no items. Please add items to your cart before submitting.');
      }

      const result = await submitOrderMutation.mutateAsync(orderId);
      setSubmittedOrderId(result.orderId);
      setEta(result.eta);
      
      // Clear cart after successful submission
      clearCart();
      
      setState('confirmed');
    } catch (error) {
      // Error handling is done in useSubmitOrder hook (toast notification)
      setState('summary');
    }
  };

  const handleStartNewOrder = () => {
    // Clear cart and navigate to menu
    clearCart();
    navigate('/menu', { replace: true });
  };

  // Loading state
  if (state === 'loading') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="w-full max-w-2xl mx-auto space-y-4">
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
          <Skeleton className="h-8 w-48 mx-auto" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    );
  }

  // Confirmation screen
  if (state === 'confirmed' && submittedOrderId) {
      // Show loading while fetching order data
      if (isLoadingOrder || !orderData) {
        return (
          <div className="container mx-auto px-4 py-8 min-h-screen flex flex-col">
            <div className="flex justify-end mb-4">
              <LanguageSwitcher />
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="w-full max-w-md mx-auto space-y-4">
                <Skeleton className="h-16 w-16 mx-auto rounded-full" />
                <Skeleton className="h-8 w-48 mx-auto" />
                <Skeleton className="h-64 w-full" />
              </div>
            </div>
          </div>
        );
      }

    return (
      <div className="container mx-auto px-4 py-8 min-h-screen flex flex-col">
        <div className="flex justify-end mb-4">
          <LanguageSwitcher />
        </div>
        <div className="flex-1 flex items-center justify-center">
          <OrderConfirmationScreen
            order={orderData}
            eta={eta}
            onStartNewOrder={handleStartNewOrder}
            restaurantName="Restaurant" // TODO: Fetch from session/branch data
            branchName={undefined} // TODO: Fetch from branch data
            branchAddress={undefined} // TODO: Fetch from branch data
            branchPhone={undefined} // TODO: Fetch from branch data
            tableNumber={null} // TODO: Fetch from session data
          />
        </div>
      </div>
    );
  }

  // Summary screen
  return (
    <div className="container mx-auto px-4 py-8 min-h-screen">
      <div className="flex justify-end mb-4">
        <LanguageSwitcher />
      </div>
      <OrderSummary
        onEdit={handleEdit}
        onConfirm={handleConfirm}
        isSubmitting={state === 'confirming'}
      />
    </div>
  );
}
